import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  RefreshCw,
  Download,
  Send,
  Users,
  ShieldCheck,
  AlertTriangle,
  Clock,
  Search,
  Loader2,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { academyApi, POSTE_LABELS } from './api';

const CELL_STYLES = {
  validee: { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  expiring_soon: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  expiring_urgent: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  expiree: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  certificat_soumis: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  refusee: { bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500' },
  not_started: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
};

const CELL_SHORT = {
  validee: '✓',
  expiring_soon: '30',
  expiring_urgent: '7',
  expiree: '✕',
  en_cours: '…',
  certificat_soumis: '?',
  refusee: '✕',
  not_started: '—',
};

const KpiCard = ({ icon: Icon, label, value, tone = 'slate', testid }) => {
  const tones = {
    emerald: 'from-emerald-50 to-white text-emerald-700 border-emerald-200',
    amber: 'from-amber-50 to-white text-amber-700 border-amber-200',
    red: 'from-red-50 to-white text-red-700 border-red-200',
    slate: 'from-slate-50 to-white text-slate-700 border-slate-200',
  };
  return (
    <div
      data-testid={testid}
      className={`p-4 rounded-lg border bg-gradient-to-br ${tones[tone]} flex items-center gap-3`}
    >
      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase font-medium tracking-wider opacity-70">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
};

const AcademyManagerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [role, setRole] = useState('');
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { only_gaps: onlyGaps };
      if (role) params.role = role;
      const res = await academyApi.managerDashboard(params);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement dashboard');
    } finally {
      setLoading(false);
    }
  }, [role, onlyGaps]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    const params = {};
    if (role) params.role = role;
    const url = academyApi.managerExportCsvUrl(params);
    const token = localStorage.getItem('dermascan_token');
    // Fetch with auth then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('Export échoué');
        return r.blob();
      })
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `academy_conformite_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        toast.success('Export CSV téléchargé');
      })
      .catch(() => toast.error("Impossible d'exporter le CSV"));
  };

  const handleSendReminders = async (dryRun) => {
    setSending(true);
    try {
      const res = await academyApi.managerSendReminders(dryRun);
      if (dryRun) {
        setReminderPreview(res);
        toast.info(
          `Aperçu : ${res.sent_j30} J-30 · ${res.sent_j7} J-7 · ${res.sent_jplus1} J+1`,
        );
      } else {
        setReminderPreview(res);
        const total = res.sent_j30 + res.sent_j7 + res.sent_jplus1;
        if (total === 0) {
          toast.info('Aucun rappel à envoyer aujourd\'hui.');
        } else {
          toast.success(`✅ ${total} rappel(s) envoyé(s)`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi rappels');
    } finally {
      setSending(false);
    }
  };

  const loadLogs = async () => {
    try {
      const res = await academyApi.managerReminderLogs(100);
      setLogs(res.items || []);
      setShowLogs(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement journal');
    }
  };

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement du tableau de bord…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="academy-manager-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testid="kpi-compliance"
          icon={ShieldCheck}
          tone="emerald"
          label="Conformité globale"
          value={`${data.kpis.compliance_pct}%`}
        />
        <KpiCard
          testid="kpi-full"
          icon={Users}
          tone="slate"
          label="Employés 100% conformes"
          value={`${data.kpis.employees_full_compliant} / ${data.kpis.employees_total}`}
        />
        <KpiCard
          testid="kpi-expiring"
          icon={Clock}
          tone="amber"
          label="Expirent ≤ 30 j"
          value={data.kpis.expiring_soon}
        />
        <KpiCard
          testid="kpi-expired"
          icon={AlertTriangle}
          tone="red"
          label="Formations expirées"
          value={data.kpis.expired}
        />
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un employé…"
              className="flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="manager-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
              data-testid="manager-role-filter"
            >
              <option value="">Tous les postes</option>
              {Object.entries(POSTE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyGaps}
              onChange={(e) => setOnlyGaps(e.target.checked)}
              data-testid="manager-only-gaps"
            />
            Seulement non conformes
          </label>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="manager-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            data-testid="manager-export-csv-btn"
          >
            <Download className="w-4 h-4" /> Exporter CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSendReminders(true)}
            disabled={sending}
            data-testid="manager-preview-reminders-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Aperçu rappels
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setConfirmOpen(true)}
            disabled={sending}
            data-testid="manager-send-reminders-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer maintenant
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadLogs}
            data-testid="manager-logs-btn"
          >
            Journal
          </Button>
        </CardContent>
      </Card>

      {/* Aperçu rappel */}
      {reminderPreview && (
        <Card>
          <CardContent className="p-4 text-sm space-y-1 bg-slate-50">
            <div className="font-semibold">
              {reminderPreview.dry_run ? '🔍 Aperçu' : '✅ Résultat envoi'} — {reminderPreview.scanned} inscription(s) scannée(s)
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-slate-600">
              <Badge variant="outline">J-30 : {reminderPreview.sent_j30}</Badge>
              <Badge variant="outline">J-7 : {reminderPreview.sent_j7}</Badge>
              <Badge variant="outline">J+1 : {reminderPreview.sent_jplus1}</Badge>
              <Badge variant="outline">Anti-doublon : {reminderPreview.skipped_dedup}</Badge>
              <Badge variant="outline">Sans email : {reminderPreview.skipped_no_email}</Badge>
              {reminderPreview.errors?.length > 0 && (
                <Badge variant="destructive">Erreurs : {reminderPreview.errors.length}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Matrice */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="manager-matrix">
            <thead>
              <tr className="bg-slate-100 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-100 z-10 min-w-[220px]">
                  Employé
                </th>
                <th className="text-left px-3 py-2 min-w-[120px]">Poste</th>
                <th className="text-center px-3 py-2 min-w-[110px]">Conformité</th>
                {data.trainings.map((t) => (
                  <th
                    key={t.id}
                    className="text-center px-2 py-2 min-w-[80px] whitespace-nowrap"
                    title={t.title}
                  >
                    <div className="text-[11px] font-medium text-slate-700 truncate max-w-[110px]">
                      {t.title}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {t.validity_months ? `${t.validity_months} mois` : '—'}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={3 + data.trainings.length} className="text-center py-10 text-slate-500">
                    Aucun employé à afficher.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.user_id} className="border-b hover:bg-slate-50">
                    <td className="px-3 py-2 sticky left-0 bg-white z-10 font-medium text-slate-800">
                      <div>{row.name}</div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[220px]">
                        {row.email || row.phone}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{POSTE_LABELS[row.poste] || row.poste}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              row.coverage_pct >= 100
                                ? 'bg-emerald-500'
                                : row.coverage_pct >= 60
                                  ? 'bg-amber-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${row.coverage_pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{row.coverage_pct}%</span>
                      </div>
                    </td>
                    {data.trainings.map((t) => {
                      const cell = row.cells[t.id];
                      const style = CELL_STYLES[cell?.status || 'not_started'];
                      return (
                        <td key={t.id} className="px-2 py-2 text-center">
                          <div
                            title={`${cell?.label || '—'}${
                              cell?.days_left != null ? ` · J${cell.days_left >= 0 ? '+' : ''}${cell.days_left}` : ''
                            }`}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-xs font-semibold ${style.bg} ${style.text}`}
                            data-testid={`cell-${row.user_id}-${t.id}`}
                          >
                            {CELL_SHORT[cell?.status || 'not_started']}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Légende */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-3 text-xs">
          {Object.entries({
            validee: 'Validée',
            expiring_soon: 'Expire ≤ 30 j',
            expiring_urgent: 'Expire ≤ 7 j',
            expiree: 'Expirée',
            en_cours: 'En cours',
            certificat_soumis: 'Certificat soumis',
            refusee: 'Refusée',
            not_started: 'Non démarrée',
          }).map(([k, label]) => (
            <div key={k} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded ${CELL_STYLES[k].bg}`} />
              <span className="text-slate-600">{label}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Logs */}
      {showLogs && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-slate-800">Journal des rappels (100 derniers)</div>
              <Button variant="ghost" size="sm" onClick={() => setShowLogs(false)}>
                Fermer
              </Button>
            </div>            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-2 py-1.5">Date</th>
                    <th className="text-left px-2 py-1.5">Type</th>
                    <th className="text-left px-2 py-1.5">Destinataire</th>
                    <th className="text-left px-2 py-1.5">Statut</th>
                    <th className="text-left px-2 py-1.5">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-6 text-slate-500">
                        Aucun rappel envoyé pour l&apos;instant.
                      </td>
                    </tr>
                  ) : (
                    logs.map((l) => (
                      <tr key={l.id} className="border-b">
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">
                          {(l.sent_at || '').slice(0, 16).replace('T', ' ')}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline">{l.flavor}</Badge>
                        </td>
                        <td className="px-2 py-1.5">{l.to_email || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span
                            className={
                              l.status === 'sent'
                                ? 'text-emerald-700'
                                : l.status === 'error'
                                  ? 'text-red-700'
                                  : 'text-amber-700'
                            }
                          >
                            {l.status}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-500">{l.error || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation avant envoi réel */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="manager-send-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer l&apos;envoi des rappels</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action envoie de vrais emails via Resend aux employés concernés
              (fenêtres J-30, J-7 et J+1). L&apos;anti-doublon empêche les envois répétés
              mais reste prudent en dehors des heures de bureau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manager-send-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="manager-send-confirm-btn"
              onClick={() => {
                setConfirmOpen(false);
                handleSendReminders(false);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Envoyer maintenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AcademyManagerDashboard;
